import type { DirectoryCourse } from './directory.ts';

/**
 * Turning what a golfer said into something the app does.
 *
 * Speech recognition on a windy fairway is lossy: it drops articles, mangles
 * proper nouns and returns lower case with no punctuation. "the palms" has to
 * find "The Palms — Sanctuary Cove" out of 1,449 names, and it must refuse
 * rather than guess when two courses are genuinely similar — sending someone to
 * the wrong course is worse than asking again.
 */

export type Command =
  | { kind: 'course'; course: DirectoryCourse; confidence: number }
  | { kind: 'hole'; hole: number }
  | { kind: 'next' }
  | { kind: 'previous' }
  | { kind: 'park' }
  | { kind: 'unpark' }
  | { kind: 'recentre' }
  | { kind: 'ambiguous'; options: DirectoryCourse[] }
  | { kind: 'unknown'; heard: string };

/** Lower case, strip accents and punctuation, collapse whitespace. */
export function normalise(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/** Words that carry no signal in a course name and only dilute a match. */
const NOISE = new Set([
  'the', 'a', 'an', 'at', 'on', 'to', 'go', 'take', 'me', 'lets', 'let', 'start',
  'play', 'playing', 'open', 'load', 'course', 'club', 'golf', 'links', 'resort',
  'country', 'and', 'of', 'please', 'im', 'i', 'am', 'we', 'are',
  // Command fillers. "start a round at the palms" must not be diluted by
  // "round" — these words say what the golfer wants done, not which course.
  'round', 'game', 'trip', 'begin', 'navigate', 'show', 'find', 'get', 'up',
  'over', 'my', 'today', 'now', 'some',
]);

const tokens = (s: string) => normalise(s).split(' ').filter(Boolean);
const meaningful = (s: string) => tokens(s).filter((w) => !NOISE.has(w));

const NUMBER_WORDS: Record<string, number> = {
  one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9,
  ten: 10, eleven: 11, twelve: 12, thirteen: 13, fourteen: 14, fifteen: 15,
  sixteen: 16, seventeen: 17, eighteen: 18,
  won: 1, tu: 2, too: 2, fore: 4, for: 4, ate: 8, // common mishearings
};

function spokenNumber(word: string): number | null {
  if (/^\d+$/.test(word)) return Number(word);
  return NUMBER_WORDS[word] ?? null;
}

/**
 * How well a spoken phrase matches a course name, 0-1.
 *
 * Deliberately token-based rather than edit-distance: recognition errors tend
 * to lose or add whole words, not letters, and "palms" matching "The Palms —
 * Sanctuary Cove" should score highly even though the strings barely resemble
 * each other.
 */
export function scoreCourse(said: string, courseName: string): number {
  const saidWords = meaningful(said);
  const nameWords = meaningful(courseName);
  if (!saidWords.length || !nameWords.length) return 0;

  let hits = 0;
  for (const w of saidWords) {
    if (nameWords.some((n) => n === w)) hits += 1;
    else if (nameWords.some((n) => n.startsWith(w) || w.startsWith(n))) hits += 0.7;
  }
  if (!hits) return 0;

  // Reward covering the spoken words; don't punish a course for having extra
  // words the golfer didn't bother saying.
  const coverage = hits / saidWords.length;
  const specificity = Math.min(1, hits / Math.min(nameWords.length, 3));
  return coverage * 0.75 + specificity * 0.25;
}

export interface ParseOptions {
  courses: DirectoryCourse[];
  /** Prefer courses near the golfer when names collide across the country. */
  near?: { lat: number; lng: number } | null;
}

const MATCH_FLOOR = 0.55;
/** Two candidates this close together are a coin toss — ask instead. */
const AMBIGUITY_GAP = 0.12;

export function parseCommand(transcript: string, opts: ParseOptions): Command {
  const said = normalise(transcript);
  if (!said) return { kind: 'unknown', heard: transcript };
  const words = tokens(said);

  // --- navigation ---------------------------------------------------------
  if (/\b(next|forward)\b/.test(said) && /\bhole\b/.test(said)) return { kind: 'next' };
  if (/\b(previous|back|last)\b/.test(said) && /\bhole\b/.test(said)) return { kind: 'previous' };

  const holeIdx = words.findIndex((w) => w === 'hole');
  if (holeIdx !== -1) {
    for (const w of words.slice(holeIdx + 1, holeIdx + 3)) {
      const n = spokenNumber(w);
      if (n != null && n >= 1 && n <= 27) return { kind: 'hole', hole: n };
    }
    // "hole" with a number before it: "take me to 5th hole"
    for (const w of words.slice(Math.max(0, holeIdx - 2), holeIdx)) {
      const n = spokenNumber(w.replace(/(st|nd|rd|th)$/, ''));
      if (n != null && n >= 1 && n <= 27) return { kind: 'hole', hole: n };
    }
  }

  if (/\b(park|parked)\b/.test(said) && !/\bunpark|clear|forget\b/.test(said)) {
    return { kind: 'park' };
  }
  if (/\b(unpark|clear the buggy|forget the buggy)\b/.test(said)) return { kind: 'unpark' };
  if (/\b(recent|recentre|recenter|centre|center|follow|where am i)\b/.test(said)) {
    return { kind: 'recentre' };
  }

  // --- course ---------------------------------------------------------------
  const scored = opts.courses
    .map((c) => ({ c, s: scoreCourse(said, c.name) }))
    .filter((x) => x.s >= MATCH_FLOOR)
    .sort((a, b) => b.s - a.s);

  if (!scored.length) return { kind: 'unknown', heard: transcript };

  // Nearby wins a tie: someone saying "the palms" is far more likely to mean
  // the course they are standing next to than one 2,000 km away.
  if (opts.near) {
    const d = (c: DirectoryCourse) =>
      (c.lat - opts.near!.lat) ** 2 + (c.lng - opts.near!.lng) ** 2;
    scored.sort((a, b) => b.s - a.s || d(a.c) - d(b.c));
  }

  const best = scored[0];
  const runnerUp = scored[1];
  if (runnerUp && best.s - runnerUp.s < AMBIGUITY_GAP) {
    const tied = scored.filter((x) => best.s - x.s < AMBIGUITY_GAP).slice(0, 4);
    // Near-identical scores across genuinely different courses: ask.
    if (tied.length > 1) return { kind: 'ambiguous', options: tied.map((x) => x.c) };
  }

  return { kind: 'course', course: best.c, confidence: best.s };
}
